/**
 * Simulation Modal
 * Modal para simular consórcio - usa o SimulatorContent reutilizável
 */

import { formatBRL as formatCurrency } from '@/lib/format';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Calculator } from 'lucide-react';
import { SimulatorContent } from './SimulatorContent';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useEffect, useState } from 'react';
import { useGetIdentity, useGetList, useNotify } from 'ra-core';
import { supabase } from '@/components/atomic-crm/providers/supabase/supabase';
import { generateProposalPdf } from './proposal-utils';
import { PorQueConsorcioDialog } from './PorQueConsorcioDialog';
import type { ProposalPayload, UserInfo } from './proposal-utils';
import { ContactCreateDialog } from '../contacts/ContactCreate';
import { getContactDetail, getContactDisplayName } from '../contacts/contact-details';
import type { Contact, Sale } from '../types';

interface Category {
  id: string;
  categoria_code: string;
  categoria_nome: string | null;
  valor_credito: string | number | null;
  prazo?: number | null;
  percentual_mensal?: string | number | null;
  parcela_sem_seguro?: string | number | null;
  valor_seguro?: string | number | null;
  total_parcela?: string | number | null;
  admin_fee_pct?: number | string | null;
}

interface SimulationModalProps {
  open: boolean;
  onClose: () => void;
  groupNumber: number;
  category: Category | null;
  isFlex: boolean;
  segment?: 'M' | 'I';
}

export const SimulationModal = ({ open, onClose, groupNumber, category, isFlex, segment = 'M' }: SimulationModalProps) => {
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const [leadId, setLeadId] = useState<string>('');
  const [selectedContactName, setSelectedContactName] = useState<string>('');
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [showContactCreate, setShowContactCreate] = useState(false);
  const [contactSearch, setContactSearch] = useState<string>('');
  const [debouncedContactSearch, setDebouncedContactSearch] = useState<string>('');
  const [lanceFixoPercent, setLanceFixoPercent] = useState<number>(30);
  const [tipoSeguro, setTipoSeguro] = useState<string | null>(null);
  const [taxaSeguro, setTaxaSeguro] = useState<number | null>(null);
  // Proposta recém-gerada, esperando o vendedor decidir o PDF "Por que consórcio".
  const [porQue, setPorQue] = useState<{ payload: ProposalPayload; userInfo: UserInfo } | null>(null);

  const { data: userRecords } = useGetList<Sale>('users', {
    filter: identity?.id ? { user_id: identity.id } : { id: -1 },
    pagination: { page: 1, perPage: 1 },
  });
  const currentUserId = userRecords?.[0]?.id;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedContactSearch(contactSearch), 300);
    return () => clearTimeout(t);
  }, [contactSearch]);

  useEffect(() => {
    const loadLanceFixoPercent = async () => {
      if (!open || !groupNumber) return;

      const { data, error } = await supabase
        .from('servopa_group_details')
        .select('lance_embutido, tipo_seguro, taxa_seguro')
        .eq('group_number', groupNumber)
        .maybeSingle();

      if (error) {
        console.error('Erro ao carregar dados do grupo:', error);
        setLanceFixoPercent(30);
        setTipoSeguro(null);
        setTaxaSeguro(null);
        return;
      }

      const parsedPercent = Number(data?.lance_embutido ?? 30);
      setLanceFixoPercent(Number.isFinite(parsedPercent) ? parsedPercent : 30);
      setTipoSeguro(data?.tipo_seguro ?? null);
      const parsedTaxa = Number(data?.taxa_seguro);
      setTaxaSeguro(Number.isFinite(parsedTaxa) ? parsedTaxa : null);
    };

    loadLanceFixoPercent();
  }, [open, groupNumber]);

  const { data: contacts, refetch: refetchContacts, isPending: isContactsPending } = useGetList<Contact>('contacts', {
    filter: debouncedContactSearch.length >= 2 ? { q: debouncedContactSearch } : { id: -999 },
    pagination: { page: 1, perPage: 30 },
    sort: { field: 'details->>name', order: 'ASC' },
  });

  const handleSelectContact = (value: string) => {
    if (value === '__create__') {
      setShowContactCreate(true);
      return;
    }

    const selected = contacts?.find((contact) => String(contact.id) === value);
    if (!selected) {
      notify('Contato selecionado não encontrado. Tente buscar novamente.', { type: 'warning' });
      return;
    }

    setLeadId(value);
    setSelectedContactName(getContactDisplayName(selected));
    setSelectedContact(selected);
    setContactSearch('');
    setDebouncedContactSearch('');
  };

  if (!category) return null;
  const valorCredito = typeof category.valor_credito === 'number' 
    ? category.valor_credito 
    : parseFloat(String(category.valor_credito || 0));
  
  const parcelaSemSeguro = typeof category.parcela_sem_seguro === 'number'
    ? category.parcela_sem_seguro
    : parseFloat(String(category.parcela_sem_seguro || 0));

  const valorSeguro = typeof category.valor_seguro === 'number'
    ? category.valor_seguro
    : parseFloat(String(category.valor_seguro || 0));

  const prazo = category.prazo || 22;
  const adminFeeRaw = typeof category.admin_fee_pct === 'number'
    ? category.admin_fee_pct
    : parseFloat(String(category.admin_fee_pct ?? ''));
  const taxaAdmin = adminFeeRaw || 25.5;
  const isAuto = segment === 'M';

  const formatTipoSeguro = (raw: string | null): string => {
    const norm = (raw || '').toUpperCase().trim();
    if (norm === 'SEGURO OBRIGATORIO') return 'Obrigatório';
    if (norm === 'SEGURO OPCIONAL') return 'Opcional';
    if (norm === 'SEGURO NA CONTEMPLACAO') return 'Na contemplação';
    return raw || '—';
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="!max-w-[min(1200px,97vw)] w-[97vw] h-[90vh] max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Calculator className="h-6 w-6" />
            Simulador - Grupo {groupNumber}
          </DialogTitle>
          <DialogDescription className="text-base">
            {category.categoria_code} - {category.categoria_nome}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {/* Informações do Grupo */}
          <div className="grid grid-cols-4 gap-4 p-4 bg-muted rounded-lg text-sm">
            <div>
              <div className="text-muted-foreground mb-1">Grupo</div>
              <div className="font-semibold flex items-center gap-2">
                {groupNumber}
                {isFlex && <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded">FLEX</span>}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">Prazo</div>
              <div className="font-semibold">{prazo}m</div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">Crédito</div>
              <div className="font-semibold">{formatCurrency(valorCredito)}</div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">Seguro</div>
              <div className="font-semibold">{formatTipoSeguro(tipoSeguro)}</div>
            </div>
          </div>

          <div className="space-y-2 border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="simulation-contact-search" className="text-red-600">Contato para proposta *</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => handleSelectContact('__create__')}
              >
                + Criar contato
              </Button>
            </div>

            {leadId ? (
              <div className="flex items-center justify-between rounded-md border px-3 py-2 text-sm bg-muted/40">
                <span>{selectedContactName}</span>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground text-xs ml-4"
                  onClick={() => {
                    setLeadId('');
                    setSelectedContactName('');
                    setSelectedContact(null);
                    setContactSearch('');
                    setDebouncedContactSearch('');
                  }}
                >
                  Trocar
                </button>
              </div>
            ) : (
              <div className="space-y-1">
                <Input
                  id="simulation-contact-search"
                  placeholder="Digite o nome do contato..."
                  value={contactSearch}
                  onChange={(e) => setContactSearch(e.target.value)}
                  className={contactSearch === '' ? 'border-red-500' : ''}
                  autoComplete="off"
                />
                {debouncedContactSearch.length >= 2 && (
                  <div className="rounded-md border bg-background shadow-sm max-h-48 overflow-y-auto">
                    {isContactsPending ? (
                      <div className="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground">
                        <Spinner size="small" />
                        Buscando contatos...
                      </div>
                    ) : contacts && contacts.length > 0 ? (
                      contacts.map((contact) => (
                        <button
                          key={contact.id}
                          type="button"
                          className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors"
                          onClick={() => handleSelectContact(String(contact.id))}
                        >
                          {getContactDisplayName(contact)}
                        </button>
                      ))
                    ) : (
                      <p className="px-3 py-2 text-sm text-muted-foreground">Nenhum contato encontrado.</p>
                    )}
                  </div>
                )}
                {debouncedContactSearch.length < 2 && contactSearch.length > 0 && (
                  <p className="text-xs text-muted-foreground">Mínimo de 2 caracteres para a busca.</p>
                )}
                {contactSearch === '' && (
                  <p className="text-sm text-red-600">Selecione um contato para gerar a proposta.</p>
                )}
              </div>
            )}
          </div>

          {/* Simulador Content */}
          <SimulatorContent
            valorCredito={valorCredito}
            parcelaSemSeguro={parcelaSemSeguro}
            valorSeguro={valorSeguro}
            prazo={prazo}
            taxaAdmin={taxaAdmin}
            isFlex={isFlex}
            isAuto={isAuto}
            tipoSeguro={tipoSeguro}
            taxaSeguro={taxaSeguro}
            fixedLancePercent={lanceFixoPercent}
            showGerarProposta={true}
            onGerarProposta={async (data) => {
              if (!selectedContact) {
                notify('Selecione um contato para gerar a proposta.', { type: 'warning' });
                return;
              }

              const userId = identity?.id;
              let userInfo = { name: identity?.fullName, email: identity?.email, phone: null as string | null };

              if (userId) {
                const { data: userRow, error } = await supabase
                  .from('users')
                  .select('name,email,phone')
                  .eq('user_id', userId as string)
                  .maybeSingle();

                if (!error && userRow) {
                  userInfo = {
                    name: userRow.name,
                    email: userRow.email,
                    phone: (userRow as { phone?: string | null }).phone ?? null,
                  };
                }
              }

              const contactName = getContactDisplayName(selectedContact);
              const proposalPayload = {
                groupNumber,
                categoria: category.categoria_code,
                modalidade: segment === 'M' ? 'Automovel' : 'Imovel',
                prazo,
                taxaAdmin,
                parcelaSemSeguro,
                valorSeguro,
                contactId: selectedContact.id,
                clientName: contactName,
                clientEmail: String(getContactDetail(selectedContact, 'email', '') || ''),
                clientPhone: String(getContactDetail(selectedContact, 'phone', '') || ''),
                generatedAt: new Date().toISOString(),
                ...data,
                // Override after spread so joint values take precedence over single-cota props
                valorCredito: data.valorCreditoJunto ?? valorCredito,
              };

              const { error: saveError } = await supabase
                .from('proposal_simulations')
                .insert({
                  contact_id: selectedContact.id,
                  sales_person_id: currentUserId ?? null,
                  group_number: groupNumber,
                  category_code: category.categoria_code,
                  prazo,
                  valor_credito: data.valorCreditoJunto ?? valorCredito,
                  generated_at: new Date().toISOString(),
                  proposal_payload: proposalPayload,
                  simulation_input: {
                    seguro: data.seguro,
                    juncaoCota: data.juncaoCota,
                    lanceLivre: data.lanceLivre,
                    lanceEmbutido: data.lanceEmbutido,
                    lanceFixoPercent: data.lanceFixoPercent,
                    descontoFlex: data.descontoFlex,
                    mesesContemplacao: data.mesesContemplacao,
                    lanceFixoAtivo: data.lanceFixoAtivo,
                    lanceLivreAtivo: data.lanceLivreAtivo,
                    // Sem isto, reabrir a proposta salva perderia a fidelidade
                    // em silêncio: o PDF gerado teria o bloco e o snapshot não.
                    lanceFidelidadeAtivo: data.lanceFidelidadeAtivo,
                    lanceFidelidadePercent: data.lanceFidelidadePercent,
                  },
                  contact_snapshot: {
                    id: selectedContact.id,
                    name: contactName,
                    email: getContactDetail(selectedContact, 'email', null),
                    phone: getContactDetail(selectedContact, 'phone', null),
                    person_type: getContactDetail(selectedContact, 'person_type', null),
                    cpf: getContactDetail(selectedContact, 'cpf', null),
                  },
                  advisor_snapshot: userInfo,
                } as never);

              if (saveError) {
                notify(`Não foi possível salvar a proposta: ${saveError.message}`, { type: 'error' });
                return;
              }

              await generateProposalPdf(
                proposalPayload,
                userInfo,
              );
              setPorQue({ payload: proposalPayload, userInfo });
            }}
          />
        </div>

        <DialogFooter className="px-6 py-4 border-t">
          <Button
            onClick={onClose}
            className="bg-[#354660] text-white hover:bg-[#29374B] dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/90"
          >
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>

      <PorQueConsorcioDialog
        payload={porQue?.payload ?? null}
        userInfo={porQue?.userInfo ?? {}}
        onClose={() => setPorQue(null)}
      />

      <ContactCreateDialog
        open={showContactCreate}
        onClose={() => setShowContactCreate(false)}
        showLeadDialog={false}
        onSuccess={async (contact) => {
          setShowContactCreate(false);
          setLeadId(String(contact.id));
          setSelectedContactName(getContactDisplayName(contact));
          setSelectedContact(contact);
          setContactSearch('');
          setDebouncedContactSearch('');
          await refetchContacts();
        }}
      />
    </Dialog>
  );
};
